import React, {useState, useEffect, useMemo} from 'react';
import {
  Grid,
  Card,
  Box,
  Button,
  IconButton,
  Tooltip,
  TextField,
  FormControlLabel,
  Switch,
  Divider,
  Alert,
  Chip,
  Paper,
  Tabs,
  Tab,
  Snackbar,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Checkbox,
  Radio,
  RadioGroup,
  FormLabel,
  FormGroup,
  Typography,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormHelperText,
} from '@mui/material';
import {
  Save,
  Preview,
  History,
  Undo,
  Redo,
  ContentCopy,
  Delete,
  Add,
  Image,
  Link as LinkIcon,
  ColorLens,
  FormatBold,
  FormatItalic,
  FormatListBulleted,
  Close,
} from '@mui/icons-material';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDButton from 'components/MDButton';
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import DashboardNavbar from 'examples/Navbars/DashboardNavbar';
import Footer from 'examples/Footer';
import {useParams, useNavigate, useLocation} from 'react-router-dom';
import {Axios} from 'services/axios';
import {Block} from 'notiflix';
import {useForm, Controller, useFieldArray} from 'react-hook-form';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import {ChromePicker} from 'react-color';
import sectionContentApi from 'api/sectionContentApi';
import SectionHistoryModal from './SectionHistoryModal';
import SectionPreviewModal from './SectionPreviewModal';
import {useCountry} from 'context/CountryContext';
import FieldRenderer from 'components/Cms/FieldRenderer';
import CountryDropdown from 'components/country/CountryDropdown';

const ColorPicker = ({value, onChange, label, error, helperText}) => {
  const [showPicker, setShowPicker] = useState(false);
  const [loading, setIsLoading] = useState(false);

  return (
    <Box>
      <MDTypography
        variant="caption"
        color={error ? 'error' : 'text'}
        gutterBottom>
        {label}
      </MDTypography>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          p: 1,
          border: '1px solid',
          borderColor: error ? 'error.main' : 'grey.300',
          borderRadius: 1,
          cursor: 'pointer',
          bgcolor: error ? 'error.light' : 'transparent',
        }}
        onClick={() => setShowPicker(!showPicker)}>
        <Box
          sx={{
            width: 30,
            height: 30,
            bgcolor: value || '#ffffff',
            border: '1px solid',
            borderColor: 'grey.400',
            borderRadius: 1,
          }}
        />
        <MDTypography variant="body2">{value || '#ffffff'}</MDTypography>
      </Box>
      {showPicker && (
        <Box sx={{position: 'absolute', zIndex: 2, mt: 1}}>
          <Box
            sx={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
            }}
            onClick={() => setShowPicker(false)}
          />
          <ChromePicker
            color={value || '#ffffff'}
            onChange={color => onChange(color.hex)}
          />
        </Box>
      )}
      {error && (
        <MDTypography
          variant="caption"
          color="error"
          sx={{mt: 0.5, display: 'block'}}>
          {helperText}
        </MDTypography>
      )}
    </Box>
  );
};

const NewSectionDialog = ({open, onClose, onSelect, pageId}) => {
  const {countries, loading: countriesLoading, getCountryById} = useCountry();

  const [templates, setTemplates] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [loadingTemplates, setLoadingTemplates] = useState(false);

  useEffect(() => {
    if (selectedCountry) {
      fetchTemplatesForCountry(selectedCountry);
    } else {
      setTemplates([]);
      setSelectedTemplate('');
    }
  }, [selectedCountry]);

  const fetchTemplatesForCountry = async countryId => {
    setLoadingTemplates(true);
    try {
      const response = await Axios.fetch(
        `/section-templates/by-country/${countryId}`,
      );
      setTemplates(response.data.data.templates || []);
    } catch (error) {
      console.error('Error fetching templates:', error);
    } finally {
      setLoadingTemplates(false);
    }
  };

  const handleCreate = () => {
    if (!selectedCountry) {
      alert('Please select a country');
      return;
    }
    if (!selectedTemplate) {
      alert('Please select a template');
      return;
    }
    onSelect(selectedTemplate, selectedCountry);
  };

  const selectedCountryData = getCountryById(selectedCountry);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <MDTypography variant="h6">Add New Section</MDTypography>
          <IconButton onClick={onClose} size="small">
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <FormControl fullWidth required>
              <InputLabel>Select Country</InputLabel>
              <Select
                value={selectedCountry}
                label="Select Country *"
                onChange={e => setSelectedCountry(e.target.value)}
                disabled={countriesLoading}>
                {countries.map(country => (
                  <MenuItem key={country.id} value={country.id}>
                    {country.flagEmoji} {country.countryName} (
                    {country.countryCode})
                  </MenuItem>
                ))}
              </Select>
              {countriesLoading && <LinearProgress sx={{mt: 1}} />}
            </FormControl>
          </Grid>

          {selectedCountryData && (
            <Grid item xs={12}>
              <Alert severity="info" icon={selectedCountryData.flagEmoji}>
                Selected: {selectedCountryData.countryName}
              </Alert>
            </Grid>
          )}

          <Grid item xs={12}>
            <FormControl
              fullWidth
              required
              disabled={!selectedCountry || loadingTemplates}>
              <InputLabel>Select Template</InputLabel>
              <Select
                value={selectedTemplate}
                label="Select Template *"
                onChange={e => setSelectedTemplate(e.target.value)}>
                {templates.map(template => (
                  <MenuItem key={template.id} value={template.id}>
                    {template.name} ({template.typeKey})
                  </MenuItem>
                ))}
              </Select>
              {loadingTemplates && <LinearProgress sx={{mt: 1}} />}
            </FormControl>
          </Grid>

          {selectedCountry && templates.length === 0 && !loadingTemplates && (
            <Grid item xs={12}>
              <Alert severity="warning">
                No templates available for this country.
                <Button
                  color="warning"
                  size="small"
                  onClick={() =>
                    (window.location.href = '/section-templates/new')
                  }
                  sx={{ml: 1}}>
                  Create Template
                </Button>
              </Alert>
            </Grid>
          )}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          onClick={handleCreate}
          variant="contained"
          color="primary"
          disabled={!selectedCountry || !selectedTemplate || countriesLoading}>
          Add Section
        </Button>
      </DialogActions>
    </Dialog>
  );
};

function SectionContentEditor() {
  const {sectionId} = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const {getCountryById} = useCountry();

  const [section, setSection] = useState(null);
  const [template, setTemplate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });
  const [history, setHistory] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(-1);

  const [imageFiles, setImageFiles] = useState({});
  const [galleryFiles, setGalleryFiles] = useState({});

  const [hasImageChanges, setHasImageChanges] = useState(false);
  const [hasGalleryChanges, setHasGalleryChanges] = useState(false);

  const [historyModalOpen, setHistoryModalOpen] = useState(false);
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [newSectionDialogOpen, setNewSectionDialogOpen] = useState(false);

  const [availableTemplates, setAvailableTemplates] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState('');
  const [originalCountry, setOriginalCountry] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [loadingTemplates, setLoadingTemplates] = useState(false);

  const [useDefaultContent, setUseDefaultContent] = useState(false);
  const [defaultContent, setDefaultContent] = useState(null);

  const parseRawContent = rawContent => {
    if (!rawContent) return null;
    const keys = Object.keys(rawContent);
    if (keys.length === 0) return null;

    const isCharMap = keys.every(k => !isNaN(k));
    if (isCharMap) {
      try {
        const str = keys
          .sort((a, b) => Number(a) - Number(b))
          .map(k => rawContent[k])
          .join('');
        return JSON.parse(str);
      } catch (e) {
        console.error('Content parse failed:', e);
        return null;
      }
    }
    return rawContent;
  };

  const {
    register,
    handleSubmit,
    control,
    watch,
    setValue,
    getValues,
    reset,
    formState: {errors, isDirty},
  } = useForm({
    defaultValues: {
      content: {},
      settings: {},
    },
  });

  useEffect(() => {
    if (sectionId && sectionId !== 'new') {
      fetchSectionData();
    } else {
      setNewSectionDialogOpen(true);
      setLoading(false);
    }
  }, [sectionId]);

  useEffect(() => {
    if (selectedCountry && !section) {
      fetchTemplatesForCountry(selectedCountry);
    }
  }, [selectedCountry, section]);

  useEffect(() => {
    if (loading) {
      Block.circle('#section-editor', 'Loading Section...');
    } else {
      Block.remove('#section-editor');
    }
  }, [loading]);

  const fetchTemplatesForCountry = async countryId => {
    setLoadingTemplates(true);
    try {
      const response = await Axios.fetch(
        `/section-templates/by-country/${countryId}`,
      );
      setAvailableTemplates(response.data.data.templates || []);

      if (response.data.data.templates.length === 0) {
        showSnackbar(
          `No templates available for selected country. Please create a template first.`,
          'warning',
        );
      }
    } catch (error) {
      console.error('Error fetching templates:', error);
      showSnackbar('Error loading templates', 'error');
    } finally {
      setLoadingTemplates(false);
    }
  };

  const fetchSectionData = async () => {
    try {
      setLoading(true);
      const res = await Axios.fetch(`sections/${sectionId}`);
      const sectionData = res.data.data.section;

      setSection(sectionData);
      setTemplate(sectionData.template);
      setSelectedCountry(sectionData.countryId || '');
      setOriginalCountry(sectionData.countryId || '');

      // Parse default content
      let parsedDefault = null;
      if (sectionData.defaultContentVersion?.content) {
        const dc = sectionData.defaultContentVersion.content;
        parsedDefault =
          typeof dc === 'string' ? JSON.parse(dc) : parseRawContent(dc);
      }
      setDefaultContent(parsedDefault);

      const parsedCustom = parseRawContent(sectionData.content);
      const hasCustomContent =
        parsedCustom && Object.keys(parsedCustom).length > 0;

      const initialUseDefault =
        location.state?.useDefaultContent ?? !hasCustomContent;
      setUseDefaultContent(initialUseDefault);

      let contentToLoad = {};
      if (initialUseDefault) {
        contentToLoad = parsedDefault || {};
      } else {
        contentToLoad = parsedCustom || {};
      }

      reset({
        content: contentToLoad,
        settings: sectionData.settings || {},
      });

      setHistory([
        {
          content: contentToLoad,
          settings: sectionData.settings || {},
          timestamp: new Date(),
        },
      ]);
      setHistoryIndex(0);
    } catch (error) {
      console.error('Error fetching section:', error);
      showSnackbar('Error loading section', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleTemplateSelect = templateId => {
    const template = availableTemplates.find(t => t.id === templateId);
    setSelectedTemplate(template);

    // Reset form with new template structure
    reset({
      content: template?.defaultStructure || {},
      settings: template?.settingsSchema || {},
    });
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({open: true, message, severity});
  };

  const handleUndo = () => {
    if (historyIndex > 0) {
      const newIndex = historyIndex - 1;
      const historyItem = history[newIndex];
      reset({
        content: historyItem.content,
        settings: historyItem.settings,
      });
      setHistoryIndex(newIndex);
    }
  };

  const handleRedo = () => {
    if (historyIndex < history.length - 1) {
      const newIndex = historyIndex + 1;
      const historyItem = history[newIndex];
      reset({
        content: historyItem.content,
        settings: historyItem.settings,
      });
      setHistoryIndex(newIndex);
    }
  };

  const handleImageFileChange = (fieldKey, device, file) => {
    setImageFiles(prev => ({
      ...prev,
      [fieldKey]: {
        ...(prev[fieldKey] || {}),
        [device]: file,
      },
    }));
    setHasImageChanges(true);
  };

  const handleGalleryFilesChange = (fieldKey, files) => {
    setGalleryFiles(prev => ({
      ...prev,
      [fieldKey]: files,
    }));
    setHasGalleryChanges(true);
  };

  const generateFieldPathKey = (field, currentPath) => {
    if (!currentPath) return field.key;
    return currentPath.replace(/\./g, '_');
  };

  const onSubmit = async data => {
    try {
      setSaving(true);
      Block.circle('#section-editor', 'Saving...');

      const formData = new FormData();

      formData.append('content', JSON.stringify(data.content));
      formData.append('settings', JSON.stringify(data.settings));
      formData.append('changeSummary', 'Manual save');
      formData.append('useDefaultContent', JSON.stringify(useDefaultContent));

      // Send countryId always (empty string = All Countries); templateId only for new sections
      formData.append('countryId', selectedCountry || '');
      if (!section) {
        formData.append('templateId', selectedTemplate.id);
      }

      Object.entries(imageFiles).forEach(([fieldKey, deviceFiles]) => {
        Object.entries(deviceFiles).forEach(([device, file]) => {
          if (file) {
            const fieldName = `image_${fieldKey}_${device}`;
            formData.append(fieldName, file);
          }
        });
      });

      Object.entries(galleryFiles).forEach(([fieldKey, deviceFiles]) => {
        Object.entries(deviceFiles).forEach(([device, files]) => {
          if (files && files.length > 0) {
            files.forEach((file, index) => {
              const fieldName = `gallery_${fieldKey}_${device}_${index}`;
              formData.append(fieldName, file);
            });
          }
        });
      });

      let response;
      if (section) {
        response = await sectionContentApi.updateSection(sectionId, formData);
      } else {
        response = await Axios.post(
          `/pages/${location.state?.pageId || ''}/sections`,
          formData,
          {
            headers: {'Content-Type': 'multipart/form-data'},
          },
        );
      }

      const updatedSection = response?.data?.data?.section || response?.section;

      let updatedParsedDefault = defaultContent;
      if (updatedSection.defaultContentVersion?.content) {
        const dc = updatedSection.defaultContentVersion.content;
        updatedParsedDefault =
          typeof dc === 'string' ? JSON.parse(dc) : parseRawContent(dc);
        setDefaultContent(updatedParsedDefault);
      }

      const updatedParsedCustom = parseRawContent(updatedSection.content);

      const contentAfterSave = useDefaultContent
        ? updatedParsedDefault || {}
        : updatedParsedCustom || data.content || {};

      reset({
        content: contentAfterSave,
        settings: updatedSection.settings || {},
      });

      setSection(updatedSection);
      setTemplate(updatedSection.template);

      setHistory(prev => [
        ...prev.slice(0, historyIndex + 1),
        {
          content: data.content,
          settings: data.settings,
          timestamp: new Date(),
        },
      ]);
      setHistoryIndex(prev => prev + 1);

      // Clear file states after save
      setImageFiles({});
      setGalleryFiles({});
      setHasGalleryChanges(false);
      setHasImageChanges(false);
      setNewSectionDialogOpen(false);

      Block.remove('#section-editor');
      showSnackbar(
        section ? 'Section saved successfully' : 'Section created successfully',
      );
    } catch (error) {
      console.error('Error saving section:', error);
      Block.remove('#section-editor');
      showSnackbar(
        error.response?.data?.message ||
          error.message ||
          'Error saving section',
        'error',
      );
    } finally {
      setSaving(false);
    }
  };

  const handleRestoreVersion = async versionId => {
    try {
      Block.circle('#section-editor', 'Restoring version...');

      const response = await sectionContentApi.restoreSectionVersion(
        sectionId,
        versionId,
        `Restored to version ${versionId}`,
      );

      reset({
        content: response.section.content,
        settings: response.section.settings,
      });

      setHistory(prev => [
        ...prev,
        {
          content: response.section.content,
          settings: response.section.settings,
          timestamp: new Date(),
        },
      ]);
      setHistoryIndex(prev => prev + 1);

      showSnackbar('Version restored successfully');
    } catch (error) {
      console.error('Restore error:', error);
      showSnackbar('Error restoring version', 'error');
    } finally {
      Block.remove('#section-editor');
    }
  };

  const handleNewSectionClose = () => {
    setNewSectionDialogOpen(false);
    if (!section) {
      navigate(-1);
    }
  };

  const handleDefaultContentToggle = newValue => {
    setUseDefaultContent(newValue);

    const currentSettings = getValues('settings');

    if (newValue) {
      reset({
        content: defaultContent || {},
        settings: currentSettings,
      });
      showSnackbar(
        'Default content loaded. Turn the toggle OFF to switch to custom content.',
        'info',
      );
    } else {
      const parsedCustom = parseRawContent(section?.content);
      const hasRealCustom =
        parsedCustom && Object.keys(parsedCustom).length > 0;

      reset({
        content: hasRealCustom ? parsedCustom : {},
        settings: currentSettings,
      });
      showSnackbar(
        hasRealCustom
          ? 'The previous custom content has been loaded. You can edit it now.'
          : 'Custom mode is active. Please fill in the fields below.',
        'info',
      );
    }
  };

  const getCurrentFieldDefinitions = () => {
    if (section && template) {
      return template.fieldDefinitions;
    }
    if (selectedTemplate) {
      return selectedTemplate.fieldDefinitions;
    }
    return [];
  };

  const selectedCountryData = getCountryById(selectedCountry);

  if (loading) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <MDBox pt={6} pb={3}>
          <LinearProgress />
        </MDBox>
        <Footer />
      </DashboardLayout>
    );
  }

  const fieldDefinitions = getCurrentFieldDefinitions();

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <MDBox pt={6} pb={3} id="section-editor">
        <Grid container spacing={3}>
          <NewSectionDialog
            open={newSectionDialogOpen}
            onClose={handleNewSectionClose}
            onSelect={(templateId, countryId) => {
              handleTemplateSelect(templateId);
              setSelectedCountry(countryId);
            }}
            pageId={location.state?.pageId}
          />

          {!section && selectedCountry && (
            <Grid item xs={12}>
              <Card sx={{mb: 3}}>
                <MDBox p={3}>
                  <Grid container spacing={3} alignItems="center">
                    <Grid item xs={12} md={4}>
                      <Box display="flex" alignItems="center" gap={2}>
                        <MDTypography variant="h6">
                          Selected Country:
                        </MDTypography>
                        {selectedCountryData && (
                          <Chip
                            label={selectedCountryData.countryName}
                            icon={<span>{selectedCountryData.flagEmoji}</span>}
                            color="info"
                          />
                        )}
                      </Box>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <FormControl fullWidth disabled={loadingTemplates}>
                        <InputLabel>Select Template</InputLabel>
                        <Select
                          value={selectedTemplate?.id || ''}
                          label="Select Template"
                          onChange={e => handleTemplateSelect(e.target.value)}>
                          {availableTemplates.map(template => (
                            <MenuItem key={template.id} value={template.id}>
                              {template.name} ({template.typeKey})
                            </MenuItem>
                          ))}
                        </Select>
                        {loadingTemplates && <LinearProgress sx={{mt: 1}} />}
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      {selectedTemplate && (
                        <MDTypography variant="body2" color="textSecondary">
                          Template: {selectedTemplate.name} •{' '}
                          {selectedTemplate.category}
                        </MDTypography>
                      )}
                    </Grid>

                    {availableTemplates.length === 0 && !loadingTemplates && (
                      <Grid item xs={12}>
                        <Alert severity="warning">
                          No templates available for this country.
                          <Button
                            color="warning"
                            size="small"
                            onClick={() =>
                              navigate('/section-templates/new', {
                                state: {countryId: selectedCountry},
                              })
                            }>
                            Create Template
                          </Button>
                        </Alert>
                      </Grid>
                    )}
                  </Grid>
                </MDBox>
              </Card>
            </Grid>
          )}

          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info">
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} md={6}>
                    <MDTypography variant="h6" color="white">
                      {section
                        ? `Edit Section: ${template?.name}`
                        : 'Create New Section'}
                    </MDTypography>
                    {section && (
                      <MDTypography variant="body2" color="white" opacity={0.8}>
                        Page: {section.page?.name} • Type: {template?.typeKey}
                        {section.country && (
                          <Chip
                            size="small"
                            label={section.country.countryName}
                            icon={<span>{section.country.flagEmoji}</span>}
                            sx={{
                              ml: 1,
                              bgcolor: 'rgba(255,255,255,0.2)',
                              color: 'white',
                            }}
                          />
                        )}
                      </MDTypography>
                    )}
                  </Grid>
                  <Grid item xs={12} md={6} sx={{textAlign: 'right'}}>
                    <Box display="flex" gap={1} justifyContent="flex-end">
                      <Tooltip title="Undo">
                        <IconButton
                          color="white"
                          onClick={handleUndo}
                          disabled={historyIndex <= 0}>
                          <Undo />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Redo">
                        <IconButton
                          color="white"
                          onClick={handleRedo}
                          disabled={historyIndex >= history.length - 1}>
                          <Redo />
                        </IconButton>
                      </Tooltip>
                      {section && (
                        <>
                          <Tooltip title="View History">
                            <IconButton
                              color="white"
                              onClick={() => setHistoryModalOpen(true)}>
                              <History />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}
                      <MDButton
                        variant="gradient"
                        color="dark"
                        onClick={() => navigate(-1)}>
                        Back
                      </MDButton>
                    </Box>
                  </Grid>
                </Grid>
              </MDBox>

              {(section || selectedTemplate) && (
                <>
                  <Box sx={{borderBottom: 1, borderColor: 'divider'}}>
                    <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)}>
                      <Tab label="Content" />
                      <Tab label="Settings" />
                    </Tabs>
                  </Box>

                  <form onSubmit={handleSubmit(onSubmit)}>
                    <MDBox p={3}>
                      {tabValue === 0 && section && (
                        <Box mb={3}>
                          <Paper
                            sx={{
                              p: 2.5,
                              bgcolor: useDefaultContent
                                ? '#e3f2fd'
                                : '#f3e5f5',
                              border: '1px solid',
                              borderColor: useDefaultContent
                                ? 'info.light'
                                : 'secondary.light',
                              borderRadius: 2,
                            }}>
                            <Box
                              display="flex"
                              justifyContent="space-between"
                              alignItems="flex-start">
                              <Box>
                                <MDTypography
                                  variant="body2"
                                  fontWeight="bold"
                                  gutterBottom>
                                  {useDefaultContent
                                    ? '📋 Default Content Mode'
                                    : '✏️ Custom Content Mode'}
                                </MDTypography>
                                <MDTypography
                                  variant="caption"
                                  color="textSecondary">
                                  {useDefaultContent
                                    ? "The template's default content is currently being used. Turn the toggle OFF to switch to custom content."
                                    : 'Custom content mode is active. You can edit your content in the fields below.'}
                                </MDTypography>
                                {useDefaultContent && defaultContent && (
                                  <MDTypography
                                    variant="caption"
                                    color="info"
                                    sx={{display: 'block', mt: 0.5}}>
                                    ℹ️ Default content version:{' '}
                                    {section?.defaultContentVersion?.version ||
                                      'N/A'}
                                  </MDTypography>
                                )}
                              </Box>
                              <FormControlLabel
                                control={
                                  <Switch
                                    checked={useDefaultContent}
                                    onChange={e =>
                                      handleDefaultContentToggle(
                                        e.target.checked,
                                      )
                                    }
                                    color="primary"
                                  />
                                }
                                label={
                                  <MDTypography
                                    variant="caption"
                                    fontWeight="bold">
                                    {useDefaultContent ? 'Default' : 'Custom'}
                                  </MDTypography>
                                }
                                labelPlacement="start"
                                sx={{ml: 2, mr: 0}}
                              />
                            </Box>

                            {!useDefaultContent && (
                              <Box
                                mt={1.5}
                                p={1}
                                bgcolor="warning.light"
                                borderRadius={1}
                                display="flex"
                                alignItems="center"
                                gap={1}>
                                <MDTypography variant="caption" color="white">
                                  ⚠️ In custom mode, you must fill in all the
                                  required fields below.
                                </MDTypography>
                              </Box>
                            )}
                          </Paper>
                        </Box>
                      )}
                      {tabValue === 0 && (
                        <Grid container spacing={3}>
                          <Grid item xs={12}>
                            <Box
                              sx={{
                                opacity: useDefaultContent ? 0.6 : 1,
                                pointerEvents: useDefaultContent
                                  ? 'none'
                                  : 'auto',
                                position: 'relative',
                              }}>
                              {useDefaultContent && (
                                <Box
                                  sx={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    right: 0,
                                    bottom: 0,
                                    zIndex: 1,
                                    cursor: 'not-allowed',
                                  }}
                                />
                              )}
                              <Grid container spacing={3}>
                                {fieldDefinitions?.map(field => {
                                  // console.log("field : ", field);
                                  return (
                                    <Grid item xs={12} md={12} key={field.key}>
                                      <FieldRenderer
                                        field={field}
                                        control={control}
                                        register={register}
                                        watch={watch}
                                        setValue={setValue}
                                        errors={errors}
                                        namePrefix="content"
                                        imageFiles={imageFiles}
                                        onImageFileChange={
                                          handleImageFileChange
                                        }
                                        galleryFiles={galleryFiles}
                                        onGalleryFilesChange={
                                          handleGalleryFilesChange
                                        }
                                      />
                                    </Grid>
                                  );
                                })}
                              </Grid>
                            </Box>

                            {(!fieldDefinitions ||
                              fieldDefinitions.length === 0) && (
                              <Paper
                                sx={{
                                  p: 4,
                                  textAlign: 'center',
                                  bgcolor: 'grey.50',
                                }}>
                                <MDTypography color="text">
                                  No fields defined in this template.
                                </MDTypography>
                              </Paper>
                            )}
                          </Grid>
                        </Grid>
                      )}

                      {tabValue === 1 && (
                        <Grid container spacing={3}>
                          {section && (
                            <Grid item xs={12} md={6}>
                              <CountryDropdown
                                value={selectedCountry || ''}
                                onChange={setSelectedCountry}
                                label="Country"
                                showAllOption={true}
                                autoSelectDefault={false}
                              />
                            </Grid>
                          )}
                          <Grid item xs={12} md={4}>
                            <FormControl fullWidth size="small">
                              <InputLabel>Padding Top</InputLabel>
                              <Controller
                                name="settings.padding.top"
                                control={control}
                                render={({field}) => (
                                  <Select
                                    label="Padding Top"
                                    {...field}
                                    value={field.value || 'medium'}>
                                    <MenuItem value="none">None</MenuItem>
                                    <MenuItem value="small">Small</MenuItem>
                                    <MenuItem value="medium">Medium</MenuItem>
                                    <MenuItem value="large">Large</MenuItem>
                                  </Select>
                                )}
                              />
                            </FormControl>
                          </Grid>
                          <Grid item xs={12} md={4}>
                            <FormControl fullWidth size="small">
                              <InputLabel>Padding Bottom</InputLabel>
                              <Controller
                                name="settings.padding.bottom"
                                control={control}
                                render={({field}) => (
                                  <Select
                                    label="Padding Bottom"
                                    {...field}
                                    value={field.value || 'medium'}>
                                    <MenuItem value="none">None</MenuItem>
                                    <MenuItem value="small">Small</MenuItem>
                                    <MenuItem value="medium">Medium</MenuItem>
                                    <MenuItem value="large">Large</MenuItem>
                                  </Select>
                                )}
                              />
                            </FormControl>
                          </Grid>
                          <Grid item xs={12} md={4}>
                            <Controller
                              name="settings.bgColor"
                              control={control}
                              render={({field}) => (
                                <ColorPicker
                                  value={field.value || '#ffffff'}
                                  onChange={field.onChange}
                                  label="Background Color"
                                />
                              )}
                            />
                          </Grid>
                          <Grid item xs={12} md={4}>
                            <FormControl fullWidth size="small">
                              <InputLabel>Text Alignment</InputLabel>
                              <Controller
                                name="settings.textAlign"
                                control={control}
                                render={({field}) => (
                                  <Select
                                    label="Text Alignment"
                                    {...field}
                                    value={field.value || 'left'}>
                                    <MenuItem value="left">Left</MenuItem>
                                    <MenuItem value="center">Center</MenuItem>
                                    <MenuItem value="right">Right</MenuItem>
                                  </Select>
                                )}
                              />
                            </FormControl>
                          </Grid>
                          <Grid item xs={12} md={4}>
                            <FormControl fullWidth size="small">
                              <InputLabel>Container Width</InputLabel>
                              <Controller
                                name="settings.containerWidth"
                                control={control}
                                render={({field}) => (
                                  <Select
                                    label="Container Width"
                                    {...field}
                                    value={field.value || 'full'}>
                                    <MenuItem value="full">Full Width</MenuItem>
                                    <MenuItem value="boxed">Boxed</MenuItem>
                                    <MenuItem value="narrow">Narrow</MenuItem>
                                  </Select>
                                )}
                              />
                            </FormControl>
                          </Grid>
                          <Grid item xs={12} md={4}>
                            <TextField
                              fullWidth
                              size="small"
                              label="Custom CSS Class"
                              {...register('settings.customCss')}
                              placeholder="my-custom-class"
                            />
                          </Grid>
                        </Grid>
                      )}

                      <Divider sx={{my: 3}} />

                      <Box display="flex" justifyContent="space-between">
                        <MDTypography variant="caption" color="text">
                          {isDirty || hasGalleryChanges || hasImageChanges
                            ? 'Unsaved changes'
                            : 'All changes saved'}
                        </MDTypography>
                        <MDButton
                          type="submit"
                          variant="gradient"
                          color="success"
                          startIcon={<Save />}
                          disabled={
                            saving ||
                            (!useDefaultContent &&
                              !isDirty &&
                              !hasGalleryChanges &&
                              !hasImageChanges &&
                              selectedCountry === originalCountry) ||
                            (!section && !selectedTemplate)
                          }>
                          {saving
                            ? 'Saving...'
                            : section
                            ? 'Save Changes'
                            : 'Create Section'}
                        </MDButton>
                      </Box>
                    </MDBox>
                  </form>
                </>
              )}

              {!section && !selectedTemplate && !loading && (
                <MDBox p={5} textAlign="center">
                  <MDTypography variant="h5" color="textSecondary" gutterBottom>
                    Please select a country and template to create a section
                  </MDTypography>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => setNewSectionDialogOpen(true)}
                    sx={{mt: 2}}>
                    Select Template
                  </Button>
                </MDBox>
              )}
            </Card>
          </Grid>
        </Grid>
      </MDBox>

      <SectionHistoryModal
        open={historyModalOpen}
        onClose={() => setHistoryModalOpen(false)}
        sectionId={sectionId}
        onRestore={handleRestoreVersion}
      />

      <SectionPreviewModal
        open={previewModalOpen}
        onClose={() => setPreviewModalOpen(false)}
        sectionId={sectionId}
        formData={getValues()}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({...snackbar, open: false})}
        anchorOrigin={{vertical: 'bottom', horizontal: 'center'}}>
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar({...snackbar, open: false})}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      <Footer />
    </DashboardLayout>
  );
}

export default SectionContentEditor;
